import React, { useState, useMemo } from "react";
import {
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Box,
  Skeleton,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { useAppSelector } from "@/store";
import { useGetAllTradeDataQuery, useGetDashboardManufacturersQuery, useGetManufacturerHtsBreakdownQuery } from "@/services";
import { roles } from "@/roles";
import { getCountryFlag } from "@/constants/country-flags";
import { HTSCodeWithTooltip } from "@/components/hts-code-with-tooltip";
import { tableCompactSx } from "@/components/table/table-styles";
import StatsBar from "@/components/stats-bar";
import SegmentedTabs from "@/components/segmented-tabs";
import { tokens } from "@/tokens";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

// Calculate total duty using the same logic as backend dashboardVisibility service
const calculateLineDuty = (line: TradeDataTypes): number => {
  const safeNum = (val: any) => {
    if (val === null || val === undefined) return 0;
    const num = typeof val === 'string' ? parseFloat(val) : Number(val);
    return isNaN(num) ? 0 : num;
  };
  return (
    safeNum(line.mfnDuty) +
    safeNum(line.mtbDuty) +
    safeNum(line.duty201) +
    safeNum(line.duty232) +
    safeNum(line.duty301) +
    safeNum(line.oth99Duties)
  );
};

const formatDate = (value: string | Date | null | undefined) => {
  if (!value) return '-';
  const parsed = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  const date = parsed instanceof Date ? parsed : new Date(parsed);
  if (isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

const getInitials = (name: string) => {
  if (!name) return '??';
  const words = name.split(' ');
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getCountryFromMID = (mid: string): string => {
  if (mid && mid.length >= 2) {
    return mid.substring(0, 2).toUpperCase();
  }
  return 'XX';
};

const DETAIL_PAGE_SIZE = 50;

// Detail View Component — fetches trade data on demand for a single manufacturer
interface SupplierDetailViewProps {
  vendor: IVendor;
  clientId?: string;
  tenantId?: string;
  onBack: () => void;
}

const DETAIL_TABS = [
  { id: "products", label: "Products by HTS Code" },
  { id: "entries", label: "Recent Entries" },
];

const SupplierDetailView: React.FC<SupplierDetailViewProps> = ({ vendor, clientId, tenantId, onBack }) => {
  const [page, setPage] = useState(0);
  const [detailTab, setDetailTab] = useState("products");

  const mid = vendor.manufacturer;
  const country = vendor.country || getCountryFromMID(mid);

  // Fetch HTS breakdown from backend aggregation (all data, no pagination)
  const { data: htsBreakdownData, isLoading: htsLoading } = useGetManufacturerHtsBreakdownQuery(
    {
      input: {
        clientId,
        tenantId,
        manufacturerID: mid,
      },
    },
    { skip: !clientId || !mid }
  );

  const productsByHts: IHtsBreakdown[] = htsBreakdownData?.data || [];

  // Fetch this manufacturer's trade data on demand for recent entries
  const { data: tradeDataResponse, isLoading: tradeLoading, isFetching } = useGetAllTradeDataQuery(
    {
      pageNo: page + 1,
      pageSize: DETAIL_PAGE_SIZE,
      clientId,
      tenantId,
      filters: { manufacturerID: { value: mid, operator: "equals" } },
    },
    { skip: !clientId || !mid }
  );

  const tradeData = tradeDataResponse?.getAllTradeData?.data || [];
  const totalCount = tradeDataResponse?.getAllTradeData?.meta?.countDocs || 0;
  const isLoading = htsLoading || tradeLoading;

  // Group lines by entry number for recent entries display
  const recentEntries = useMemo(() => {
    const entriesMap = new Map<string, { entryNumber: string; date: Date | null; lineCount: number; totalValue: number; totalDuty: number }>();

    tradeData.forEach((line) => {
      const entryNum = line.entrySummaryNumber || 'Unknown';
      if (!entriesMap.has(entryNum)) {
        entriesMap.set(entryNum, {
          entryNumber: entryNum,
          date: line.entryDate ? new Date(line.entryDate) : null,
          lineCount: 0,
          totalValue: 0,
          totalDuty: 0,
        });
      }
      const entry = entriesMap.get(entryNum)!;
      entry.lineCount += 1;
      entry.totalValue += line.lineTariffGoodsValueAmount || 0;
      entry.totalDuty += calculateLineDuty(line);
    });

    return Array.from(entriesMap.values())
      .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
      .slice(0, 10);
  }, [tradeData]);

  return (
    <Stack sx={{ height: '100%', overflow: 'auto' }}>
      <Box sx={{ p: 2 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            mb: 2,
            color: tokens.text.secondary,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          Back to Manufacturers
        </Button>

        {/* Supplier Header Card */}
        <Box
          sx={{
            border: `1px solid ${tokens.border.container}`,
            borderRadius: '12px',
            mb: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: tokens.bg.hover,
                    color: tokens.brand.primary,
                    fontSize: '1.25rem',
                    fontWeight: tokens.fontWeight.bold,
                    borderRadius: tokens.radius.md,
                  }}
                >
                  {getInitials(vendor.manufacturerName || mid)}
                </Avatar>
                <Box>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography variant="h6" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary }}>
                      {vendor.manufacturerName || mid}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={3} sx={{ color: tokens.text.secondary }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ fontSize: '1rem' }}>
                        {getCountryFlag(country)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: tokens.text.primary }}>
                        {country}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <BusinessIcon sx={{ fontSize: 14, color: tokens.text.secondary }} />
                      <Typography variant="body2" sx={{ color: tokens.text.secondary }}>Manufacturer</Typography>
                    </Stack>
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: tokens.bg.muted,
                        px: 1,
                        py: 0.5,
                        color: tokens.text.primary,
                        fontSize: tokens.fontSize.xs,
                      }}
                    >
                      MID: {mid}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Stack>
          </Box>

          {/* Stats Row — uses pre-aggregated data from IVendor */}
          <Box sx={{ borderTop: `1px solid ${tokens.border.light}` }}>
            <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
              <Box sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} sx={{ color: tokens.text.primary }}>
                  {vendor.htsCodeCount ?? 0}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                  HTS Codes
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} sx={{ color: tokens.text.primary }}>
                  {formatCurrency(vendor.tariffValue)}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                  Total Value
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} sx={{ color: tokens.text.primary }}>
                  {(vendor.entryCount ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                  Entries
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} sx={{ color: tokens.text.primary }}>
                  {(vendor.lineCount ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                  Lines
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={tokens.fontWeight.heavy} sx={{ color: tokens.text.primary }}>
                  {formatCurrency(vendor.tariffDuty)}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                  Total Duty
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Loading indicator for trade data */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && (
          <>
            {/* Fetching indicator */}
            {isFetching && (
              <Box sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={14} sx={{ color: tokens.text.muted }} />
                  <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Updating...</Typography>
                </Stack>
              </Box>
            )}

            {/* Tabbed Table — Products / Entries */}
            <Box sx={{
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.default}`,
              overflow: "hidden",
              ...tableCompactSx,
            }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${tokens.border.light}` }}>
                <SegmentedTabs
                  tabs={DETAIL_TABS}
                  activeTab={detailTab}
                  onChange={setDetailTab}
                />
              </Box>

              {detailTab === "products" && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>HTS Code</TableCell>
                        <TableCell align="center">Lines</TableCell>
                        <TableCell align="right">Total Value</TableCell>
                        <TableCell align="right">Total Duty</TableCell>
                        <TableCell>Last Entry</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productsByHts.map((product) => (
                        <TableRow key={product.htsCode}>
                          <TableCell>
                            <HTSCodeWithTooltip htsCode={product.htsCode}>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: tokens.fontWeight.bold,
                                  fontSize: tokens.fontSize.base,
                                  color: tokens.brand.primary,
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {product.htsCode}
                              </Typography>
                            </HTSCodeWithTooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>{product.lineCount}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {formatCurrency(product.totalValue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {formatCurrency(product.totalDuty)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.base }}>
                              {formatDate(product.lastEntryDate)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {detailTab === "entries" && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Entry Number</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Lines</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="right">Duty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentEntries.map((entry) => (
                        <TableRow key={entry.entryNumber}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {entry.entryNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.base }}>
                              {formatDate(entry.date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {entry.lineCount}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {formatCurrency(entry.totalValue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {formatCurrency(entry.totalDuty)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Trade Data Pagination */}
            {totalCount > DETAIL_PAGE_SIZE && (
              <Box sx={{ border: `1px solid ${tokens.border.container}`, borderRadius: '12px', mt: 2 }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={DETAIL_PAGE_SIZE}
                  rowsPerPageOptions={[DETAIL_PAGE_SIZE]}
                  sx={{
                    '.MuiTablePagination-toolbar': { minHeight: 40 },
                    '.MuiTablePagination-displayedRows': { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

// Main Suppliers Tab Component
const SuppliersTab: React.FC = () => {
  const [selectedVendor, setSelectedVendor] = useState<IVendor | null>(null);

  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const clientId = useMemo(() => {
    if (isTDAdminTCD) {
      return selectedTenant?._id;
    }
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
      return user?._id;
    }
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  // Fetch manufacturers using the backend aggregation (runs across ALL trade data)
  const { data: manufacturersData, isLoading, isFetching } = useGetDashboardManufacturersQuery(
    {
      dashboardVisibility: {
        clientId,
        ...(isTDAdminTCD && selectedTenant && { tenantId: selectedTenant._id }),
      },
    },
    {
      skip: !clientId,
    }
  );

  const manufacturers: IVendor[] = manufacturersData?.data || [];

  // Stats calculations from manufacturer aggregation
  const stats = useMemo(() => {
    const totalSuppliers = manufacturers.length;
    const uniqueCountries = new Set(
      manufacturers.map(m => m.country || getCountryFromMID(m.manufacturer))
    ).size;
    const totalValue = manufacturers.reduce((sum, m) => sum + (m.tariffValue || 0), 0);
    const totalEntries = manufacturers.reduce((sum, m) => sum + (m.entryCount || 0), 0);
    const totalLines = manufacturers.reduce((sum, m) => sum + (m.lineCount || 0), 0);
    return { totalSuppliers, uniqueCountries, totalValue, totalEntries, totalLines };
  }, [manufacturers]);

  const handleRowClick = (vendor: IVendor) => {
    setSelectedVendor(vendor);
  };

  const handleBack = () => {
    setSelectedVendor(null);
  };

  const renderSkeletonRows = () => (
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell><Skeleton variant="rectangular" height={36} /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
      </TableRow>
    ))
  );

  // If a manufacturer is selected, show the detail view
  if (selectedVendor) {
    return (
      <SupplierDetailView
        vendor={selectedVendor}
        clientId={clientId}
        tenantId={isTDAdminTCD ? selectedTenant?._id : undefined}
        onBack={handleBack}
      />
    );
  }

  // Otherwise show the list view
  return (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Stats Bar */}
      <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 2 }}>
        <StatsBar
          isLoading={isLoading}
          compact
          items={[
            { label: "Manufacturers", value: stats.totalSuppliers.toLocaleString(), centerValue: true },
            { label: "Countries", value: stats.uniqueCountries, centerValue: true },
            { label: "Entries", value: stats.totalEntries.toLocaleString(), centerValue: true },
            { label: "Lines", value: stats.totalLines.toLocaleString(), centerValue: true },
            { label: "Total Value", value: formatCurrency(stats.totalValue), color: tokens.text.primary },
          ]}
        />
      </Box>

      {/* Loading indicator */}
      {isFetching && !isLoading && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={14} sx={{ color: tokens.text.muted }} />
            <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Updating...</Typography>
          </Stack>
        </Box>
      )}

      {/* Suppliers Table */}
      <Box sx={{ flex: 1, overflow: 'hidden', px: 2, pb: 2 }}>
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          ...tableCompactSx,
        }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>MID</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell align="center">Entries</TableCell>
                  <TableCell align="center">Lines</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell>Last Entry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? renderSkeletonRows() : (
                  <>
                    {manufacturers.map((vendor) => {
                      const country = vendor.country || getCountryFromMID(vendor.manufacturer);
                      return (
                        <TableRow
                          key={vendor.manufacturer}
                          onClick={() => handleRowClick(vendor)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: tokens.bg.hover },
                            '& > td': { borderBottom: `1px solid ${tokens.border.light}` },
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: tokens.bg.hover,
                                  color: tokens.brand.primary,
                                  fontSize: tokens.fontSize.base,
                                  fontWeight: tokens.fontWeight.bold,
                                  borderRadius: tokens.radius.sm,
                                }}
                              >
                                {getInitials(vendor.manufacturerName || vendor.manufacturer)}
                              </Avatar>
                              <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                                {vendor.manufacturerName || '-'}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {vendor.manufacturer}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography variant="body2" component="span" sx={{ fontSize: '1rem' }}>
                                {getCountryFlag(country)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                                {country}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>{(vendor.entryCount || 0).toLocaleString()}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>{(vendor.lineCount || 0).toLocaleString()}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {formatCurrency(vendor.tariffValue)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: tokens.fontSize.base }}>
                              {formatDate(vendor.lastEntryDate)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {manufacturers.length === 0 && !isLoading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                            No manufacturer data found. Upload trade data to see manufacturers.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Stack>
  );
};

export default SuppliersTab;
